/* ──────────────────────────────────────────────────────────────
   Analytics — today's activity, 7-day trend, status mix.
   Premium animated charts built on Animated Views (web + native safe).
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  Animated,
  Pressable,
  Platform,
  ActivityIndicator,
  ViewStyle,
} from 'react-native';

import { useTheme } from '../context/themeContext';
import { useReloadOnFocus } from '../navigation/useReloadOnFocus';
import { useResponsive, CONTENT } from '../lib/responsive';
import { Icon, Button, StatRow } from '../components/ui';
import { getAnalyticsOverview, AnalyticsOverview } from '../services/catalog/analytics';
import { handleApiError } from '../utils/errorHandler';

// ── Animated grouped bar chart (last 7 days) ──────────────────
function TrendChart({ series }: { series: AnalyticsOverview['series'] }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const grow = useRef(new Animated.Value(0)).current;
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    grow.setValue(0);
    Animated.timing(grow, { toValue: 1, duration: 750, useNativeDriver: false }).start();
  }, [series, grow]);

  const CHART_H = 170;
  const max = Math.max(1, ...series.flatMap(d => [d.itemsAdded, d.itemsPublished]));

  const Bar = ({ value, color, dim }: { value: number; color: string; dim: boolean }) => {
    const h = grow.interpolate({ inputRange: [0, 1], outputRange: [0, (value / max) * CHART_H] });
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
        <Animated.View
          style={{
            width: '70%',
            height: h,
            backgroundColor: color,
            opacity: dim ? 0.3 : 1,
            borderTopLeftRadius: 7,
            borderTopRightRadius: 7,
            ...Platform.select({
              // Premium vertical gradient + glow on web.
              web: { background: `linear-gradient(180deg, ${color}, ${color}99)`, boxShadow: `0 8px 18px ${color}44`, transition: 'opacity 140ms ease, transform 140ms ease' } as any,
              default: {},
            }),
          }}
        />
      </View>
    );
  };

  const fmtDate = (iso: string) =>
    new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <View style={{ position: 'relative' }}>
      {/* Hover tooltip card (web) */}
      {hovered !== null && (
        <View
          // @ts-ignore web-only positioning
          style={{
            position: 'absolute',
            bottom: CHART_H + 12,
            left: `${((hovered + 0.5) / series.length) * 100}%`,
            zIndex: 20,
            ...Platform.select({ web: { transform: 'translateX(-50%)', pointerEvents: 'none' } as any, default: {} }),
          }}
        >
          <View
            style={{
              backgroundColor: colors.ink,
              borderRadius: radii.r2,
              paddingVertical: 10,
              paddingHorizontal: 12,
              minWidth: 150,
              ...(shadows.lg as any),
            }}
          >
            <Text style={{ fontSize: fontSize.xs, color: colors.paper, opacity: 0.8, fontFamily: theme.font.sans, marginBottom: 6 }}>
              {fmtDate(series[hovered].date)}
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <View style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: colors.accent }} />
              <Text style={{ fontSize: fontSize.sm, color: colors.paper, fontFamily: theme.font.sans }}>
                Items added <Text style={{ fontWeight: fontWeight.semibold }}>{series[hovered].itemsAdded}</Text>
              </Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <View style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: colors.success }} />
              <Text style={{ fontSize: fontSize.sm, color: colors.paper, fontFamily: theme.font.sans }}>
                Published <Text style={{ fontWeight: fontWeight.semibold }}>{series[hovered].itemsPublished}</Text>
              </Text>
            </View>
          </View>
          {/* little pointer */}
          <View
            style={{
              alignSelf: 'center',
              width: 10,
              height: 10,
              backgroundColor: colors.ink,
              marginTop: -5,
              ...Platform.select({ web: { transform: 'rotate(45deg)' } as any, default: { transform: [{ rotate: '45deg' }] } }),
            }}
          />
        </View>
      )}

      <View style={{ height: CHART_H, position: 'relative' }}>
        {/* subtle horizontal gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map(t => (
          <View
            key={t}
            style={{ position: 'absolute', left: 0, right: 0, top: CHART_H * t, height: 1, backgroundColor: colors.line, opacity: t === 1 ? 0.9 : 0.5 }}
          />
        ))}
      <View style={{ flexDirection: 'row', height: CHART_H, alignItems: 'flex-end', gap: 10 }}>
        {series.map((d, i) => (
          <Pressable
            key={d.date}
            onHoverIn={() => setHovered(i)}
            onHoverOut={() => setHovered(null)}
            onPress={() => setHovered(prev => (prev === i ? null : i))}
            style={{
              flex: 1,
              flexDirection: 'row',
              gap: 3,
              alignItems: 'flex-end',
              height: '100%',
              borderRadius: radii.r2,
              backgroundColor: hovered === i ? colors.paper2 : 'transparent',
              ...Platform.select({ web: { cursor: 'pointer', transition: 'background-color 140ms ease' } as any, default: {} }),
            }}
          >
            <Bar value={d.itemsAdded} color={colors.accent} dim={hovered !== null && hovered !== i} />
            <Bar value={d.itemsPublished} color={colors.success} dim={hovered !== null && hovered !== i} />
          </Pressable>
        ))}
      </View>
      </View>
      {/* X axis day labels (Mon, Tue…) */}
      <View style={{ flexDirection: 'row', gap: 10, marginTop: 8 }}>
        {series.map((d, i) => (
          <Text
            key={d.date}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: fontSize.xs,
              color: hovered === i ? colors.ink : colors.ink3,
              fontWeight: hovered === i ? fontWeight.semibold : fontWeight.regular,
              fontFamily: theme.font.sans,
            }}
          >
            {new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' })}
          </Text>
        ))}
      </View>
      {/* Legend */}
      <View style={{ flexDirection: 'row', gap: 16, marginTop: 14, justifyContent: 'center' }}>
        <Legend color={colors.accent} label="Items added" />
        <Legend color={colors.success} label="Published" />
      </View>
    </View>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  const { theme } = useTheme();
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: color }} />
      <Text style={{ fontSize: theme.fontSize.xs, color: theme.colors.ink2, fontFamily: theme.font.sans }}>{label}</Text>
    </View>
  );
}

// ── Animated status breakdown (stacked bar + legend) ──────────
function StatusBreakdown({ byState }: { byState: Record<string, number> }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const grow = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    grow.setValue(0);
    Animated.timing(grow, { toValue: 1, duration: 700, useNativeDriver: false }).start();
  }, [byState, grow]);

  const segs = [
    { key: 'PUBLISHED', label: 'Published', color: colors.success },
    { key: 'DRAFT', label: 'Draft', color: colors.accent },
    { key: 'DEACTIVATED', label: 'Deactivated', color: colors.warn },
    { key: 'ARCHIVED', label: 'Archived', color: colors.ink4 },
  ].map(s => ({ ...s, count: byState[s.key] || 0 }));
  const total = Math.max(1, segs.reduce((a, s) => a + s.count, 0));

  return (
    <View>
      <View style={{ flexDirection: 'row', height: 24, borderRadius: 999, overflow: 'hidden', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
        {segs.map(s => {
          const w = grow.interpolate({ inputRange: [0, 1], outputRange: ['0%', `${(s.count / total) * 100}%`] });
          return (
            <Animated.View
              key={s.key}
              style={{
                width: w,
                backgroundColor: s.color,
                ...Platform.select({ web: { background: `linear-gradient(180deg, ${s.color}, ${s.color}CC)` } as any, default: {} }),
              }}
            />
          );
        })}
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14, marginTop: 14 }}>
        {segs.map(s => (
          <View key={s.key} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 1, minWidth: 0 }}>
            <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: s.color, flexShrink: 0 }} />
            <Text numberOfLines={1} style={{ flexShrink: 1, minWidth: 0, fontSize: fontSize.sm, color: colors.ink2, fontFamily: theme.font.sans }}>
              {s.label} <Text style={{ fontWeight: fontWeight.semibold, color: colors.ink }}>{s.count}</Text>
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

// ── Today stat tile (premium: gradient icon chip + hover lift) ─
function TodayTile({ label, value, icon, tone }: { label: string; value: number; icon: string; tone: string }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const [hover, setHover] = useState(false);
  return (
    <View
      // @ts-ignore web hover handlers
      onMouseEnter={Platform.OS === 'web' ? () => setHover(true) : undefined}
      onMouseLeave={Platform.OS === 'web' ? () => setHover(false) : undefined}
      style={{
        flex: 1,
        minWidth: 150,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: hover ? tone + '66' : colors.line,
        borderRadius: radii.r3,
        padding: 16,
        overflow: 'hidden',
        ...(shadows.sm as any),
        ...Platform.select({
          web: {
            transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
            transform: hover ? 'translateY(-2px)' : 'translateY(0)',
            boxShadow: hover ? `0 10px 24px ${tone}26` : undefined,
          } as any,
          default: {},
        }),
      }}
    >
      {/* faint corner glow */}
      <View
        // @ts-ignore web-only radial accent
        style={Platform.OS === 'web'
          ? ({ position: 'absolute', top: -28, right: -28, width: 90, height: 90, borderRadius: 45, background: `radial-gradient(circle, ${tone}1F, transparent 70%)` } as any)
          : { position: 'absolute', top: -28, right: -28, width: 90, height: 90, borderRadius: 45, backgroundColor: tone + '12' }}
      />
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 12 }}>
        <View
          // @ts-ignore web gradient chip
          style={{
            width: 34, height: 34, borderRadius: 10, alignItems: 'center', justifyContent: 'center',
            borderWidth: 1, borderColor: tone + '33',
            ...Platform.select({ web: { background: `linear-gradient(135deg, ${tone}2E, ${tone}14)` } as any, default: { backgroundColor: tone + '1F' } }),
          }}
        >
          <Icon name={icon} size={16} color={tone} />
        </View>
        <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink3, fontFamily: theme.font.sans }}>{label}</Text>
      </View>
      <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, letterSpacing: -0.5 }}>
        {value}
      </Text>
    </View>
  );
}

export default function AnalyticsScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const { isMobile, pad } = useResponsive();

  const [data, setData] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getAnalyticsOverview());
    } catch (err) {
      handleApiError(err, 'Could not load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useReloadOnFocus(load);

  const sectionCard: ViewStyle = {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    padding: 18,
    overflow: 'hidden', // keep all card content inside the boundary
    ...(shadows.sm as any),
  };

  // Dynamic insight line under the trend graph.
  const insight = (() => {
    if (!data) return '';
    const s = data.series;
    const todayAdded = data.today.itemsAdded;
    const weekTotal = s.reduce((a, p) => a + p.itemsAdded, 0);
    const yesterday = s.length >= 2 ? s[s.length - 2].itemsAdded : 0;
    const diff = todayAdded - yesterday;
    const trend = diff > 0 ? `up ${diff} vs yesterday` : diff < 0 ? `down ${Math.abs(diff)} vs yesterday` : 'same as yesterday';
    return `${todayAdded} item${todayAdded === 1 ? '' : 's'} added today (${trend}) · ${weekTotal} this week · ${data.today.itemsPublished} published today.`;
  })();

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ paddingHorizontal: pad, paddingTop: 24, paddingBottom: 64, maxWidth: CONTENT.wide, width: '100%', alignSelf: 'center' }}>
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1, minWidth: 220 }}>
          <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, letterSpacing: -0.02 * fontSize['3xl'] }}>
            Analytics
          </Text>
          <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 4 }}>
            Today's activity and how your catalog is trending.
          </Text>
        </View>
        <Button kind="accent" icon="refresh" onPress={load}>Refresh</Button>
      </View>

      {loading && !data ? (
        <View style={{ paddingVertical: 64, alignItems: 'center' }}>
          <ActivityIndicator color={colors.accent} size="large" />
        </View>
      ) : data ? (
        <View style={{ gap: 16 }}>
          {/* Today tiles */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
            <TodayTile label="Items added" value={data.today.itemsAdded} icon="box" tone={colors.accent} />
            <TodayTile label="Published" value={data.today.itemsPublished} icon="check-circle" tone={colors.success} />
            <TodayTile label="Item Types" value={data.today.typesCreated} icon="sliders" tone={colors.info} />
            <TodayTile label="Categories" value={data.today.categoriesCreated} icon="folder" tone={colors.warn} />
            <TodayTile label="Tags" value={data.today.tagsCreated} icon="tag" tone={colors.accent} />
            <TodayTile label="Collections" value={data.today.collectionsCreated} icon="grid" tone={colors.info} />
          </View>

          {/* Trend chart + insight */}
          <View style={sectionCard}>
            <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, marginBottom: 4 }}>
              Last 7 days
            </Text>
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans, marginBottom: 18 }}>
              Items added vs published each day.
            </Text>
            <TrendChart series={data.series} />
            <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 16 }} />
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Icon name="sparkles" size={14} color={colors.accent} />
              <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.ink2, fontFamily: theme.font.sans }}>{insight}</Text>
            </View>
          </View>

          {/* Status + totals */}
          <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 16 }}>
            <View style={[sectionCard, { flex: 1 }]}>
              <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, marginBottom: 14 }}>
                Items by status
              </Text>
              <StatusBreakdown byState={data.byState} />
            </View>
            <View style={[sectionCard, { flex: 1 }]}>
              <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, marginBottom: 14 }}>
                Catalog totals
              </Text>
              <View style={{ gap: 10 }}>
                {([
                  ['Items', data.totals.items],
                  ['Published', data.totals.published],
                  ['Item types', data.totals.types],
                  ['Categories', data.totals.categories],
                  ['Tags', data.totals.tags],
                  ['Collections', data.totals.collections],
                ] as [string, number][]).map(([label, n]) => (
                  <StatRow key={label} label={label} value={n} />
                ))}
              </View>
            </View>
          </View>
        </View>
      ) : null}
    </ScrollView>
  );
}
